import React, { useEffect, useState } from 'react';
import { db } from '../Firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { generateReferralLink } from '../api/referral';

const ReferralEarnings = () => {
  const { user } = useAuth();
  const [referralLink, setReferralLink] = useState('');
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      setError('User ID is required');
      setLoading(false);
      return;
    }

    // Generate referral link when component mounts
    const generateLink = async () => {
      try {
        const link = await generateReferralLink(user.id);
        setReferralLink(link.referralLink || link);
      } catch (err) {
        console.error('Failed to generate referral link:', err);
      }
    };

    generateLink();

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'referrals', user.id),
      (docSnap) => {
        if (docSnap.exists()) {
          setEarnings(docSnap.data());
        } else {
          setEarnings({
            totalRevenue: 0,
            userPayout: 0,
            platformCut: 0,
            referralClicks: 0
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching earnings:', error);
        setError('Failed to load earnings data');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [user?.id]);

  const copyToClipboard = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      alert('Referral link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="referral-earnings">
        <div className="loading-spinner">Loading earnings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-earnings">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="referral-earnings">
      <h3>🔗 Your Referral Link</h3>
      {referralLink && (
        <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>Share this link to earn commissions:</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <input
              type="text"
              value={referralLink}
              readOnly
              style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
            <button
              onClick={copyToClipboard}
              style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Copy
            </button>
          </div>
          <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
            Earn commissions when people sign up and use RapidMind through your link!
          </p>
        </div>
      )}

      <h3>💰 Referral Earnings</h3>
      <div className="earnings-grid">
        <div className="earnings-card">
          <h4>Total Revenue</h4>
          <p className="amount">${earnings.totalRevenue?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="earnings-card">
          <h4>Your Earnings (70%)</h4>
          <p className="amount">${earnings.userPayout?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="earnings-card">
          <h4>Platform Fee (30%)</h4>
          <p className="amount">${earnings.platformCut?.toFixed(2) || '0.00'}</p>
        </div>
        <div className="earnings-card">
          <h4>Total Clicks</h4>
          <p className="amount">{earnings.referralClicks || 0}</p>
        </div>
      </div>
      {earnings.userPayout >= 10 && (
        <div className="payout-notice">
          🎉 You've reached the minimum payout threshold of $10.00!
        </div>
      )}
    </div>
  );
};

export default ReferralEarnings;
